import React, { useEffect, useState, useRef } from "react";
import {fetchOrder, acceptOrderFromAdmin, cancelOrderFromAdmin, updateOrderFromAdmin, markArrivedFromAdmin } from "../service/api/orders";
import { useParams } from "react-router-dom";
import DeliveryPartnerCard from "../components/DeliveryPartner";
import "../css/OrderDetail.css";
import {formatDate} from "../utils/formatTime";
import useTimer from "../hooks/useTimer";
import {PrintIcon, ChevronDown, FileIcon, ManageIcon} from "../utils/icons";

import {
    Check, X, MapPin, StickyNote, Star, Pencil
} from 'lucide-react';
import EditOrderModal from "../components/EditOrderModal";
import DeliveryMap from "../components/DeliveryMap";
import {PrintBill, usePrintBill} from "../components/PrintBill";
import ChangeAddressModal from "../components/modal/ChangeAddressModal";

const STATUS_MAP = {
    1:  { label: "Placed",       status: "pending"   },
    2:  { label: "Accepted",  status: "pending"   },
    3:  { label: "Assigned",  status: "assigned"  },
    4:  { label: "Picked Up", status: "pickedup"  },
    5:  { label: "Completed", status: "completed" },
    6:  { label: "Cancelled", status: "cancelled" },
    8:  { label: "Awaiting Payment", status: "pending"},
    10: { label: "Scheduled/Slot", status: "pending"},
    11: { label: "Confirmed Order",       status: "pending"   },
    9: {label: "Payment Failed", status: "cancelled"}
};

const CANCEL_REASONS = [
    "Customer requested cancellation",
    "Item out of stock",
    "Restaurant not accepting orders",
    "Delivery not available in this area",
    "Duplicate order",
    "Payment issue",
];

const canCancelOrder = (statusId) => [1, 2, 3, 4, 11].includes(statusId);
const canAcceptOrder = (statusId) => statusId === 1 || statusId === 11;


// Add this component near the other card components

const OFFER_STATUS_STYLE = {
    done:    { bg: "#f0fdf4", color: "#16a34a", label: "Done" },
    offered: { bg: "#fefce8", color: "#b45309", label: "Offered" },
    accepted: { bg: "#bce9f8", color: "#0945b4", label: "Accepted" },
    default: { bg: "#f3f4f6", color: "#6b7280", label: "Pending"  },
};

const OFFER_CASE_INFO = {
    "Case 1": { label: "Within Radius",  color: "#6366f1" },
    "Case 2": { label: "Target Radius",  color: "#0ea5e9" },
};

const formatSlotTime = (time) => {
    if (!time) return "—";

    const [hour, minute] = String(time).split(":");
    const date = new Date();

    date.setHours(Number(hour || 0));
    date.setMinutes(Number(minute || 0));
    date.setSeconds(0);

    return date.toLocaleTimeString("en-IN", {
        hour: "2-digit",
        minute: "2-digit",
        hour12: true,
    });
};

const Skeleton = ({ w, h, mb }) => (
    <div className="skeleton" style={{ width: w, height: h, marginBottom: mb }} />
);

const Label = ({ children }) => <div className="label">{children}</div>;

const SectionTitle = ({ children }) => (
    <div className="section-title">{children}</div>
);

const Card = ({ children, delay, gradient, style}) => (
    <section className={`card${gradient ? " card--gradient" : ""}`} style={style} data-delay={delay}>
        {children}
    </section>
);


const RatingStars = ({ value }) => {
    const rating = Number(value || 0);

    if (!rating) {
        return <span className="rating-empty">Not rated</span>;
    }

    return (
        <div className="rating-stars">
            {Array.from({ length: 5 }).map((_, i) => (
                <Star
                    key={i}
                    size={14}
                    fill={i < rating ? "#f59e0b" : "none"}
                    color={i < rating ? "#f59e0b" : "#cbd5e1"}
                />
            ))}
            <span className="rating-number">{rating}/5</span>
        </div>
    );
};

const RatingRow = ({ title, rating, review }) => {
    if (!rating && !review) return null;

    return (
        <div className="rating-row">
            <div className="rating-row__top">
                <span className="rating-row__title">{title}</span>
                <RatingStars value={rating} />
            </div>

            {review && (
                <div className="rating-row__review">
                    {review}
                </div>
            )}
        </div>
    );
};

const OrderRatingsCard = ({ rating }) => {
    const hasAnyRating =
        rating?.rating_store ||
        rating?.rating_delivery ||
        rating?.delivery_rating_store ||
        rating?.delivery_rating_customer ||
        rating?.review_store ||
        rating?.review_delivery ||
        rating?.delivery_review_store ||
        rating?.delivery_review_customer;

    if (!hasAnyRating) return null;

    return (
        <Card delay="4">
            <SectionTitle>Ratings & Reviews</SectionTitle>

            <div className="ratings-list">
                <RatingRow
                    title="Customer → Store"
                    rating={rating?.rating_store}
                    review={rating?.review_store}
                />

                <RatingRow
                    title="Customer → Delivery Partner"
                    rating={rating?.rating_delivery}
                    review={rating?.review_delivery}
                />

                <RatingRow
                    title="Delivery Partner → Store"
                    rating={rating?.delivery_rating_store}
                    review={rating?.delivery_review_store}
                />

                <RatingRow
                    title="Delivery Partner → Customer"
                    rating={rating?.delivery_rating_customer}
                    review={rating?.delivery_review_customer}
                />
            </div>
        </Card>
    );
};

const PriceSummary = ({ order }) => {
    if (!order) return null;

    const subTotal = Number(order?.sub_total || 0);
    const delivery = Number(order?.delivery_charge || 0);
    const actualDelivery = Number(order?.actual_delivery_charge || delivery);
    const couponAmount = Number(order?.coupon_amount || 0);
    const surgeAmt = Number(order?.surge_details?.surge_amount || 0);

    const hasDeliveryCoupon =
        order?.coupon_name &&
        couponAmount > 0 &&
        actualDelivery > delivery;

    const serviceFee =
        order?.service_fee_type === "percent"
            ? (subTotal * Number(order?.service_fee || 0)) / 100
            : Number(order?.service_fee || 0);

    return (
        <div className="price-summary">
            <PriceRow label="Subtotal" value={subTotal} />

            <div className="price-row">
                <span>Delivery Charge</span>

                <span className="price-row__value">
                    {hasDeliveryCoupon ? (
                        <span style={{ display: "flex", flexDirection: "column", alignItems: "flex-end", gap: 2 }}>
                            <span style={{ textDecoration: "line-through", opacity: 0.55 }}>
                                ₹{actualDelivery.toFixed(2)}
                            </span>
                            <span className="price-row__value--highlight">
                                ₹{delivery.toFixed(2)}
                            </span>
                            <span style={{
                                fontSize: 11,
                                color: "#16a34a",
                                borderBottom: "1px dashed #16a34a",
                                lineHeight: 1.2,
                                fontWeight: 600,
                            }}>
                                {delivery > 0 ? "Base delivery coupon applied" : "Free delivery coupon applied"}
                            </span>
                        </span>
                    ) : (
                        <>₹{delivery.toFixed(2)}</>
                    )}
                </span>
            </div>

            {hasDeliveryCoupon && (
                <PriceRow
                    label={`Coupon (${order?.coupon_name})`}
                    value={-couponAmount}
                />
            )}

            {serviceFee > 0 && (
                <PriceRow
                    label={`Service Fee ${
                        order?.service_fee_type === "percent"
                            ? `(${order?.service_fee}%)`
                            : ""
                    }`}
                    value={serviceFee}
                />
            )}

            {order?.surge_details && surgeAmt > 0 && (
                <PriceRow label={order.surge_details.name} value={surgeAmt} />
            )}

            {order?.tip_amount && (
                <PriceRow label="Tip Amount" value={Number(order?.tip_amount)} />
            )}


            {order?.wallet_amount && (
                <PriceRow label="Paid with wallet" value={Number(order?.wallet_amount || 0)} />
            )}

            <PriceRow label="Total" value={Number(order?.total || 0)} />

            {order?.payment_mode !== "RAZORPAY" && (
                <PriceRow label="Payable" value={Number(order?.payable || order?.total || 0)} highlight />
            )}
        </div>
    );
};

const InfoField = ({ label, value, mono, link }) => (
    <div className="info-field">
        <Label>{label}</Label>

        {link ? (
            <a
                href={link}
                className={`value${mono ? " mono" : ""}`}
                target="_blank"
                rel="noopener noreferrer"
            >
                {value || "—"}
            </a>
        ) : (
            <div className={`value${mono ? " mono" : ""}`}>
                {value || "—"}
            </div>
        )}
    </div>
);

const StatBox = ({label, value, variant = "primary"}) => (
    <div className={`stat-box stat-box--${variant}`}>
        <Label>{label}</Label>
        <div className="stat-box__value">{value}</div>
    </div>
);

const getTimingStyle = (totalMinutes, orderstauts_id) => {
    if([1, 2, 3, 4, 5, 8, 10, 11].includes(orderstauts_id)) {
        if (totalMinutes < 30) return {color: "#22c55e", label: "On Time", labelBg: "#f0fdf4", labelColor: "#16a34a"};
        if (totalMinutes < 45) return {color: "#f59e0b", label: "Late", labelBg: "#fffbeb", labelColor: "#b45309"};
        return {color: "#ef4444", label: "Too Late", labelBg: "#fef2f2", labelColor: "#dc2626"};
    }
    return {
        color: "#ef4444", label: STATUS_MAP[orderstauts_id]?.label || "Cancelled", labelBg: "#fef2f2", labelColor: "#dc2626"
    }
};

const OrderItem = ({ name, price, quantity, addons }) => (
    <div className="order-item">
        <div className="order-item__left">
            <span className="order-item__qty">{quantity}×</span>
            <span className="order-item__name">{name}</span>
        </div>
        <span className="price-row__value price-row__value--highlight">
          ₹{Number(price)?.toFixed(2)}
        </span>

        {addons?.length > 0 && (
            <div className="order-item__addons">
                {addons.map((addon, i) => (
                    <div key={i} className="order-item__addon-row">
                        <span className="order-item__addon-name">
                            + {addon.addon_name}
                            {addon.addon_category_name && (
                                <span className="order-item__addon-category">
                                    ({addon.addon_category_name})
                                </span>
                            )}
                        </span>
                        <span className="order-item__addon-price">
                            +₹{Number(addon.addon_price)?.toFixed(2)}
                        </span>
                    </div>
                ))}
            </div>
        )}
    </div>
);

const PriceRow = ({ label, value, highlight }) => (
    <div className={`price-row${highlight ? " price-row--highlight" : ""}`}>
        <span>{label}</span>
        <span className={`price-row__value${highlight ? " price-row__value--highlight" : ""}`}>
            {Number(value) < 0
                ? `-₹${Math.abs(Number(value)).toFixed(2)}`
                : `₹${Number(value)?.toFixed(2)}`}
        </span>
    </div>
);

// Before: const TimelineItem = ({ time, text, isLast, properties }) => {
const TimelineItem = ({ time, text, isLast, properties, order }) => {
    // Parse properties — may arrive as a string or object
    const props = (() => {
        if (!properties) return {};
        if (typeof properties === 'string') {
            try { return JSON.parse(properties); } catch { return {}; }
        }
        return properties;
    })();

    const type             = props.type ?? '';
    const isDelivered      = type === 'Order_Delivered';
    const distanceKm       = props?.distance;
    const deliveryLat      = props?.delivery_lat;
    const deliveryLng      = props?.delivery_lng;

    // Build Google Maps directions URL
    const mapsUrl = (() => {
        if (!deliveryLat || !deliveryLng) return null;

        const origin = `${deliveryLat},${deliveryLng}`;

        if (isDelivered) {
            // Rider → customer location
            const customerLoc = (() => {
                try { return JSON.parse(order?.location || '{}'); } catch { return {}; }
            })();
            const destLat = customerLoc?.lat;
            const destLng = customerLoc?.lng;
            if (destLat && destLng) {
                return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${destLat},${destLng}&travelmode=driving`;
            }
            return `https://www.google.com/maps?q=${origin}`;
        }

        // All other events: rider → restaurant
        const restLat = order?.restaurant?.latitude;
        const restLng = order?.restaurant?.longitude;
        if (restLat && restLng) {
            return `https://www.google.com/maps/dir/?api=1&origin=${origin}&destination=${restLat},${restLng}&travelmode=driving`;
        }
        return `https://www.google.com/maps?q=${origin}`;
    })();

    const mapLabel = isDelivered ? 'Rider → Customer' : 'Rider → Restaurant';


    return (
        <div className={`timeline-item${isLast ? ' timeline-item--last' : ''}`}>
            <div className="timeline-track">
                {!isLast && <div className="timeline-line" />}
                <div className="timeline-dot" />
            </div>
            <div className="timeline-content">
                <span className="timeline-time">
                    {time &&
                        new Date(time).toLocaleTimeString('en-IN', {
                            hour: '2-digit',
                            minute: '2-digit',
                            hour12: true,
                        })}
                </span>
                <div className="timeline-text">{text}</div>

                {/* Distance + map button row */}
                {(distanceKm || mapsUrl) && (
                    <div className="timeline-meta">
                        {distanceKm && (
                            <span className="timeline-distance">
                                {/* ruler icon */}
                                <svg className="timeline-distance__icon" viewBox="0 0 16 16" fill="none" stroke="currentColor" strokeWidth="1.8" strokeLinecap="round" strokeLinejoin="round">
                                    <path d="M2 8h12M2 8l3-3M2 8l3 3M14 8l-3-3M14 8l-3 3" />
                                </svg>
                                {distanceKm} km
                            </span>
                        )}

                        {mapsUrl && (
                            <a
                            className="timeline-map-btn"
                            href={mapsUrl}
                            target="_blank"
                            rel="noopener noreferrer"
                            title={`View ${mapLabel} on map`}
                            >
                        {/* pin icon */}
                            <svg className="timeline-map-btn__icon" viewBox="0 0 16 16" fill="currentColor">
                            <path d="M8 1a5 5 0 0 0-5 5c0 3.5 5 9 5 9s5-5.5 5-9a5 5 0 0 0-5-5zm0 6.5a1.5 1.5 0 1 1 0-3 1.5 1.5 0 0 1 0 3z"/>
                            </svg>
                        {mapLabel}
                            </a>
                            )}
                    </div>
                    )}
            </div>
        </div>
    );
};

const OrderStatus = ({ statusId }) => {
    const { label, status } = STATUS_MAP[statusId] ?? { label: "Unknown", status: "pending" };
    return (
        <div className={`order-status order-status--${status}`}>
            <Label>Status</Label>
            <div className="order-status__label">
                <span className="order-status__dot" />
                {label}
            </div>
        </div>
    );
};

const CancelReasonModal = ({ order, refundType, onConfirm, onClose, loading }) => {
    const [selected, setSelected] = useState("");
    const [custom,   setCustom]   = useState("");

    const isOther     = selected === "other";
    const finalReason = isOther ? custom.trim() : selected;
    const canSubmit   = finalReason.length >= 3 && !loading;

    const total     = parseFloat(order?.total ?? 0);
    const walletAmt = parseFloat(order?.wallet_amount ?? 0);

    const refundLabel = {
        NOREFUND: walletAmt > 0 ? `₹${walletAmt.toFixed(2)} wallet only` : "NO REFUND",
        HALF:     `₹${(total / 2).toFixed(2)} half refund`,
        FULL:     `₹${total.toFixed(2)} full refund`,
    }[refundType] ?? "NO REFUND";

    return (
        <div className="modal-overlay" onClick={onClose}>
            <div className="modal-box" onClick={(e) => e.stopPropagation()}>

                {/* Header */}
                <div className="modal-header">
                    <div>
                        <h3 className="modal-title">Cancel the order with {refundLabel}?</h3>
                    </div>
                    <button className="modal-close" onClick={onClose} disabled={loading}>✕</button>
                </div>

                {/* Body */}
                <div className="modal-body">
                    <div className="modal-section-label">Select Reason for Cancellation</div>

                    <div className="modal-reasons">
                        {CANCEL_REASONS.map((r) => (
                            <button
                                key={r}
                                className={`modal-reason-chip${selected === r ? " is-active" : ""}`}
                                onClick={() => setSelected(r)}
                            >
                                {r}
                            </button>
                        ))}
                        <button
                            className={`modal-reason-chip${isOther ? " is-active" : ""}`}
                            onClick={() => setSelected("other")}
                        >
                            Other…
                        </button>
                    </div>

                    {isOther && (
                        <textarea
                            className="modal-textarea"
                            placeholder="Describe the reason…"
                            value={custom}
                            onChange={(e) => setCustom(e.target.value)}
                            rows={2}
                            autoFocus
                        />
                    )}
                </div>

                {/* Footer */}
                <div className="modal-footer">
                    <button className="btn" onClick={onClose} disabled={loading}>Keep Order</button>
                    <button
                        className="action-btn action-btn--danger"
                        disabled={!canSubmit}
                        onClick={() => onConfirm(finalReason)}
                    >
                        {loading ? "Cancelling…" : "Confirm Cancel"}
                    </button>
                </div>

            </div>
        </div>
    );
};

const OrderActionsCard = ({ order, onOrderUpdate, showToast, setLoading, onAccept, accepting, markingArrived, onMarkArrived  }) => {
    const [dropOpen,   setDropOpen]   = useState(false);
    const [refundType, setRefundType] = useState(null);
    const [showModal,  setShowModal]  = useState(false);
    const [cancelling, setCancelling] = useState(false);
    const dropRef = useRef(null);

    const showArrivedBtn = order?.orderstatus_id === 3 && !order?.is_arrived;
    const canAccept = canAcceptOrder(order?.orderstatus_id);

    useEffect(() => {
        const handler = (e) => {
            if (dropRef.current && !dropRef.current.contains(e.target)) setDropOpen(false);
        };
        document.addEventListener("mousedown", handler);
        return () => document.removeEventListener("mousedown", handler);
    }, []);

    const total= parseFloat(order?.total ?? 0);

    const handleRefundPick = (type) => {
        setRefundType(type);
        setDropOpen(false);
        setShowModal(true);
    };

    const handleConfirm = async (reason) => {
        setCancelling(true);
        setLoading("Order Cancelling...");
        try {
            const res = await cancelOrderFromAdmin(order.id, reason, refundType);
            await onOrderUpdate?.();
            showToast("success", res?.message || "Success");
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong";
            showToast("error", message);
        } finally {
            setShowModal(false);
            setCancelling(false);
        }
    };

    if (!canCancelOrder(order?.orderstatus_id)) return null;

    return (
        <>
            <Card delay="3" style={{zIndex: 500}}>
                <Label>Order Actions</Label>

                {showArrivedBtn && (
                    <button
                        className="action-btn action-btn--accept"
                        style={{ marginBottom: 10, width: "100%", background: "#0ea5e9" }}
                        disabled={markingArrived}
                        onClick={onMarkArrived}
                    >
                        {markingArrived ? (
                            "Marking…"
                        ) : (
                            <>
                                {/* eslint-disable-next-line react/jsx-no-undef */}
                                <MapPin size={14} />
                                Mark Arrived at Restaurant
                            </>
                        )}
                    </button>
                )}


                <div className="oa-wrap" ref={dropRef}>
                    {canAccept && (
                        <button className="action-btn action-btn--accept" disabled={accepting} onClick={onAccept}>
                            {accepting ? "Accepting…" : "✓ Accept Order"}
                        </button>
                    )}
                    <button
                        className={`oa-trigger${dropOpen ? " is-open" : ""}`}
                        onClick={() => setDropOpen((v) => !v)}
                    >
                        <span className="oa-trigger-x">✕</span>
                            Cancel Order
                        <ChevronDown open={dropOpen} />
                    </button>

                    {dropOpen && (
                        <div className="oa-dropdown">
                            <div className="oa-dropdown-label">Choose refund type</div>

                            <button className="oa-refund-row" onClick={() => handleRefundPick("NOREFUND")}>
                                <div className="oa-refund-row__info">
                                    <span className="oa-refund-row__name">No Refund</span>
                                </div>
                                <span className="oa-refund-row__amt">₹0</span>
                            </button>

                            <div className="oa-sep" />

                            <button className="oa-refund-row" onClick={() => handleRefundPick("HALF")}>
                                <div className="oa-refund-row__info">
                                    <span className="oa-refund-row__name">Half Refund</span>
                                    <span className="oa-refund-row__sub">50% of order total</span>
                                </div>
                                <span className="oa-refund-row__amt oa-refund-row__amt--green">
                                    ₹{(total / 2).toFixed(2)}
                                </span>
                            </button>

                            <div className="oa-sep" />

                            <button className="oa-refund-row" onClick={() => handleRefundPick("FULL")}>
                                <div className="oa-refund-row__info">
                                    <span className="oa-refund-row__name">Full Refund</span>
                                    <span className="oa-refund-row__sub">Complete order amount</span>
                                </div>
                                <span className="oa-refund-row__amt oa-refund-row__amt--green">
                                    ₹{total.toFixed(2)}
                                </span>
                            </button>
                        </div>
                    )}
                </div>
            </Card>

            {showModal && (
                <CancelReasonModal
                    order={order}
                    refundType={refundType}
                    onConfirm={handleConfirm}
                    onClose={() => !cancelling && setShowModal(false)}
                    loading={cancelling}
                />
            )}
        </>
    );
};

const PageHeader = ({ order, loading, setModalOpen, triggerPrint, triggerThermal}) => {

    return (
        <header className="page-header">
            <div>
                <p className="page-header__eyebrow">Order Details</p>
                <h1 className="page-header__title">
                    {loading ? <Skeleton w={180} h={22} /> : (order?.unique_order_id || "—")}
                </h1>
            </div>
            <div className="page-header__actions">
                {[1, 2, 3, 8, 10, 11].includes(order?.orderstatus_id) && (
                    <button className="btn" onClick={() => setModalOpen("MANAGE_ORDERS")}>
                        <ManageIcon /> Manage Order
                    </button>
                )}
                <button className="btn" onClick={triggerPrint}><PrintIcon/> Print Bill</button>
                <button className="btn" onClick={triggerThermal}><FileIcon/> Thermal Bill</button>
            </div>
        </header>
    );
};

function StatusBadge({status }) {
    const online = status === 1;
    return (
        <span style={{
            display: "inline-flex", alignItems: "center", gap: 5,
            fontSize: 11, fontWeight: 600, letterSpacing: "0.03em",
            color:      online ? "#059669" : "#EF4444",
            flexShrink: 0,
        }}>
          <span style={{
              width: 6, height: 6, borderRadius: "50%",
              background: online ? "#10B981" : "#EF4444",
              display: "inline-block",
          }} />
            {online ? "Online" : "Offline"}
        </span>
    );
}

const OfferedOrdersCard = ({ offeredOrders }) => {
    if (!offeredOrders?.length) return null;

    const sorted = [...offeredOrders].sort((a, b) => {
        if (a.status === "done" && b.status !== "done") return -1;
        if (b.status === "done" && a.status !== "done") return 1;
        return new Date(a.created_at) - new Date(b.created_at);
    });

    const fmtTime = (iso) => {
        if (!iso) return "—";
        return new Date(iso).toLocaleTimeString("en-IN", {
            hour: "2-digit", minute: "2-digit", second: "2-digit", hour12: true,
        });
    };

    const getDuration = (start, end) => {
        if (!start || !end) return null;
        const diff = Math.round((new Date(end) - new Date(start)) / 1000);
        if (diff < 60) return `${diff}s`;
        return `${Math.floor(diff / 60)}m ${diff % 60}s`;
    };

    return (
        <Card delay="5">
            <SectionTitle>Offered Orders ({offeredOrders.length})</SectionTitle>
            <div style={{ display: "flex", flexDirection: "column", gap: 10, marginTop: 8 }}>
                {sorted.map((offer) => {
                    const statusStyle = OFFER_STATUS_STYLE[offer.status] ?? OFFER_STATUS_STYLE.default;
                    const caseInfo    = OFFER_CASE_INFO[offer.offer_case];
                    const duration    = getDuration(offer.offered_at, offer.responded_at);

                    return (
                        <div
                            key={offer.id}
                            style={{
                                borderRadius: "var(--border-radius-md, 8px)",
                                background: "var(--color-background-secondary, #f9fafb)",
                                border: offer.status === "done"
                                    ? "1px solid #bbf7d0"
                                    : "1px solid var(--color-border, #e5e7eb)",
                                overflow: "hidden",
                            }}
                        >
                            {/* Top row: name + badges */}
                            <div style={{
                                display: "flex",
                                alignItems: "center",
                                justifyContent: "space-between",
                                padding: "9px 12px 6px",
                                gap: 8,
                            }}>
                                <div style={{ minWidth: 0 }}>
                                    <div style={{
                                        fontWeight: 600,
                                        fontSize: "0.82rem",
                                        color: "var(--color-text-primary, #111827)",
                                        whiteSpace: "nowrap",
                                        overflow: "hidden",
                                        textOverflow: "ellipsis",
                                    }}>
                                        {offer.delivery_guy?.name}
                                    </div>
                                    <div style={{
                                        fontSize: "0.73rem",
                                        color: "var(--color-text-secondary, #6b7280)",
                                        marginTop: 1,
                                    }}>
                                        {offer.delivery_guy?.phone}
                                    </div>
                                </div>

                                <div style={{ display: "flex", alignItems: "center", gap: 6, flexShrink: 0 }}>
                                    {caseInfo && (
                                        <span style={{
                                            fontSize: "0.68rem", fontWeight: 600,
                                            padding: "2px 7px", borderRadius: 99,
                                            background: caseInfo.color + "18",
                                            color: caseInfo.color,
                                            border: `1px solid ${caseInfo.color}33`,
                                        }}>
                                            {caseInfo.label}
                                        </span>
                                    )}
                                    <span style={{
                                        fontSize: "0.68rem", fontWeight: 600,
                                        padding: "2px 8px", borderRadius: 99,
                                        background: statusStyle.bg,
                                        color: statusStyle.color,
                                    }}>
                                        {statusStyle.label}
                                    </span>
                                </div>
                            </div>

                            {/* Bottom row: timestamps */}
                            <div style={{
                                display: "grid",
                                gridTemplateColumns: "1fr 1fr",
                                gap: 1,
                                borderTop: "1px solid var(--color-border, #e5e7eb)",
                                background: "var(--color-border, #e5e7eb)",
                            }}>
                                <div style={{
                                    padding: "5px 10px",
                                    background: "var(--color-background-secondary, #f9fafb)",
                                }}>
                                    <div style={{ fontSize: "0.65rem", color: "#9ca3af", fontWeight: 500, marginBottom: 1 }}>
                                        OFFERED AT
                                    </div>
                                    <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", fontVariantNumeric: "tabular-nums" }}>
                                        {fmtTime(offer.offered_at)}
                                    </div>
                                </div>

                                <div style={{
                                    padding: "5px 10px",
                                    background: "var(--color-background-secondary, #f9fafb)",
                                }}>
                                    <div style={{ fontSize: "0.65rem", color: "#9ca3af", fontWeight: 500, marginBottom: 1 }}>
                                        RESPONDED AT
                                        {duration && (
                                            <span style={{
                                                marginLeft: 5,
                                                background: "#f3f4f6",
                                                color: "#6b7280",
                                                padding: "0px 5px",
                                                borderRadius: 99,
                                                fontSize: "0.63rem",
                                            }}>
                                                +{duration}
                                            </span>
                                        )}
                                    </div>
                                    <div style={{ fontSize: "0.75rem", fontWeight: 600, color: "#374151", fontVariantNumeric: "tabular-nums" }}>
                                        {fmtTime(offer.responded_at)}
                                    </div>
                                </div>
                            </div>
                        </div>
                    );
                })}
            </div>
        </Card>
    );
};

const OrderPage = () => {
    const { orderId } = useParams();

    const { printRef, triggerPrint, thermalRef, triggerThermal  } = usePrintBill();

    const [data, setData]  = useState(null);
    const [loading,   setLoading]   = useState(null);
    const [accepting, setAccepting] = useState(false);
    const [toast,     setToast]     = useState(null);

    const [modalOpen, setModalOpen] = useState(null);

    const [addressModalOpen, setAddressModalOpen] = useState(false);
    const [markingArrived, setMarkingArrived] = useState(false);

    const handleMarkArrived = async () => {
        setMarkingArrived(true);
        setLoading("Marking arrived...");
        try {
            const res = await markArrivedFromAdmin(order?.id);
            await loadOrder();
            showToast("success", res?.message || "Marked as arrived");
        } catch (err) {
            showToast("error", err?.response?.data?.data || err?.message || "Something went wrong");
        } finally {
            setMarkingArrived(false);
            setLoading(null);
        }
    };

    const showToast = (type, message) => {
        setToast({ type, message });
        setTimeout(() => setToast(null), 3500);
    };

    const loadOrder = async () => {
        setLoading("Loading Order...");
        try {
            const response = await fetchOrder(orderId);
            setData(response?.data);
        } catch {
            showToast("error", "Failed to load order.");
        } finally {
            setLoading(null);
        }
    };

    const handleAccept = async () => {
        setAccepting(true);
        setLoading("Accepting Order...");
        try {
            const res = await acceptOrderFromAdmin(order?.id);
            await loadOrder();
            showToast("success", res?.message || "Success");
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong";
            showToast("error", message);
        } finally {
            setAccepting(false);
        }
    };

    const handleEdit = async (request) => {
        setLoading("Updating Order...");
        try {
            const res = await updateOrderFromAdmin(order?.id, request);
            await loadOrder();
            showToast("success", res?.message || "Success");
        } catch (err) {
            const message =
                err?.response?.data?.message ||
                err?.message ||
                "Something went wrong";
            showToast("error", message);
        } finally {
            setAccepting(false);
        }
    };

    useEffect(() => { loadOrder(); }, [orderId]);

    const order = data?.order;

    const { days, hours, minutes, seconds, isOngoing } = useTimer(
        order?.created_at,
        order?.orderstatus_id,
        order?.updated_at
    );

    const totalMinutes  = (days * 1440) + (hours * 60) + minutes;
    const timingStyle   = getTimingStyle(totalMinutes, order?.orderstatus_id);
    const activities = data?.activities;


    const getLocationCoords = (locationStr) => {
        try { return JSON.parse(locationStr || '{}'); }
        catch { return {}; }
    };


    return (
        <div className="order-page">
            <PageHeader order={order} loading={loading} setModalOpen={(l)=>setModalOpen(l)} triggerPrint={triggerPrint} triggerThermal={triggerThermal}/>

            <div className="order-detail-grid">
                {loading ? (
                    <Card delay="1">
                        <div className="skeleton-group">
                            <Skeleton h={22} w="55%" mb={4}/>
                            <Skeleton h={14} w="35%"/>
                        </div>
                    </Card>
                ) : (
                    <Card delay="1">
                        <div className="card-header">
                            <div className="card-header__left">
                                <h2>{order?.restaurant?.name}</h2>
                                <span className="card-header__id">{order?.unique_order_id}</span>
                            </div>
                            <div className="card-header__right">
                                <Label>Order Date</Label>
                                <div className="card-header__date">
                                    {order?.created_at && formatDate(order?.created_at)}
                                </div>
                            </div>
                        </div>

                        <div className={"divider"} />

                        <div className="order-main-row">
                            <div className="order-customer">
                                <SectionTitle>Customer Details</SectionTitle>
                                <InfoField label="Name" value={order?.user?.name}/>
                                <InfoField label="Email" value={order?.user?.email}/>
                                <InfoField label="Contact" value={order?.user?.phone}/>
                                <div className="info-field">
                                    <Label>Delivery Address</Label>
                                    <div className="value" style={{display: "flex", alignItems: "center", gap: 8}}>
                                        <span>{order?.address || "—"}</span>
                                        {order?.delivery_type === 1 && canCancelOrder(order?.orderstatus_id) && (
                                            <button
                                                className="addr-edit-btn"
                                                onClick={() => setAddressModalOpen(true)}
                                                title="Change delivery address"
                                            >
                                                <Pencil size={12}/>
                                            </button>
                                        )}
                                    </div>
                                </div>
                            </div>
                            <div className="order-status-col">
                                <OrderStatus statusId={order?.orderstatus_id}/>
                                <div className="stat-boxes">
                                    <StatBox label="Order Type"
                                             value={order?.delivery_type === 1 ? "Delivery" : "Self Pickup"}
                                             variant="accent"/>
                                    <StatBox label="Payment" value={order?.payment_mode} variant="primary"/>
                                </div>
                            </div>
                        </div>

                        <div style={{ marginTop: 20 }}>
                            <SectionTitle>Order Items</SectionTitle>
                            {order?.orderitems?.map((item, idx) => (
                                <OrderItem
                                    key={idx}
                                    name={item?.name}
                                    price={item?.price}
                                    quantity={item?.quantity}
                                    addons={item?.order_item_addons}  // ← add this
                                />
                            ))}
                        </div>


                        {order?.order_comment && (
                            <div style={{ marginTop: 16 }}>
                                <SectionTitle>Order Comment</SectionTitle>
                                <div style={{
                                    fontSize: "1.2rem",
                                    color: "var(--color-text-secondary)",
                                    background: "var(--color-background-secondary)",
                                    borderRadius: "var(--border-radius-md)",
                                    padding: "10px 14px",
                                    borderLeft: "3px solid var(--color-border-secondary)",
                                }}>
                                    {order?.order_comment}
                                </div>
                            </div>
                        )}

                        <PriceSummary order={order} />
                    </Card>
                )}


                <div>
                    {order?.payment_mode === "RAZORPAY" && (
                        <Card delay="2">
                            <SectionTitle>Razorpay Details</SectionTitle>
                            <InfoField
                                label="Order ID"
                                value={order?.razorpay_data?.razorpay_order_id_first} mono
                                link={`https://dashboard.razorpay.com/app/orders/${order?.razorpay_data?.razorpay_order_id_first}`}
                            />
                            <InfoField
                                label="Payment ID"
                                value={order?.razorpay_data?.razorpay_payment_id}
                                mono
                                link={`https://dashboard.razorpay.com/app/payments/${order?.razorpay_data?.razorpay_payment_id}`}
                            />
                        </Card>
                    )}

                    {order?.orderstatus_id === 6 && order?.cancel_reason && (
                        <Card delay="3">
                            <Label>Cancel Reason</Label>
                            <div className="distance-value" style={{ color: "#ef4444", fontSize: "0.82rem" }}>
                                {order?.cancel_reason}
                            </div>
                        </Card>
                    )}

                    <Card delay="4">
                        <Label>Distance</Label>
                        <div className="distance-value">
                            {order?.distance ? (
                                <a
                                    href={(() => {
                                        const location = getLocationCoords(order?.location);
                                        const destLat = location?.lat;
                                        const destLng = location?.lng;
                                        const originLat = order?.restaurant?.latitude;
                                        const originLng = order?.restaurant?.longitude;
                                        return `https://www.google.com/maps/dir/?api=1&origin=${originLat},${originLng}&destination=${destLat},${destLng}&travelmode=driving`;
                                    })()}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    style={{ textDecoration: 'none', color: 'inherit' }}
                                >
                            {parseFloat(order?.distance).toFixed(2)}
                                <span className="distance-unit">km from store ↗</span>
                                </a>
                                ) : "—"}
                        </div>
                    </Card>


                    {order?.delivery_slot_id && (
                        <Card delay="4">
                            <Label>Slot Order</Label>

                            <div className="distance-value" style={{ color: "#6d28d9" }}>
                                {order?.delivery_slot?.name || "Delivery Slot"}
                            </div>

                            <div style={{ marginTop: 10 }}>
                                <InfoField
                                    label="Slot Time"
                                    value={
                                        order?.delivery_slot
                                            ? `${formatSlotTime(order?.delivery_slot?.start_time)} - ${formatSlotTime(order?.delivery_slot?.end_time)}`
                                            : "—"
                                    }
                                />

                            </div>
                        </Card>
                    )}

                    <OrderActionsCard
                        order={order}
                        onOrderUpdate={loadOrder}
                        setLoading={setLoading}
                        showToast={showToast}
                        onAccept={handleAccept}
                        accepting={accepting}
                        onMarkArrived={handleMarkArrived}
                        markingArrived={markingArrived}
                    />


                    {(order?.accept_delivery) && (
                        <Card delay="4">
                            <div className="distance-value">
                                {order?.accept_delivery?.user?.name}
                                <span className="distance-unit">Assigned Guy</span>
                            </div>
                            <StatusBadge status={order?.accept_delivery?.user?.delivery_guy_detail?.status} />

                            {order?.surge_details && order.surge_details.partner_amount > 0 && (
                                <div style={{ marginTop: 8, display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                                <span style={{ fontSize: "0.78rem", color: "#6b7280" }}>
                                    Surge Share (Partner)
                                </span>
                                    <span style={{ fontSize: "0.82rem", fontWeight: 600, color: "#059669" }}>
                                    +₹{Number(order?.partner_surge_amount ?? 0).toFixed(2)}
                                </span>
                                </div>
                            )}
                        </Card>
                    )}


                    <OrderRatingsCard rating={order?.rating} />

                    {order?.customer_feedback && (
                        <Card delay="4">
                            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                                <Label>Feedback</Label>
                                {order.customer_feedback.rating && (
                                    <div style={{ display: 'flex', gap: 2 }}>
                                        {Array.from({ length: 5 }).map((_, i) => (
                                            <Star
                                                key={i}
                                                size={13}
                                                fill={i < order.customer_feedback.rating ? '#f59e0b' : 'none'}
                                                color={i < order.customer_feedback.rating ? '#f59e0b' : '#cbd5e1'}
                                            />
                                        ))}
                                    </div>
                                )}
                            </div>
                            {order.customer_feedback.feedback_payload && (
                                <div className="distance-value">
                                    <span style={{ fontSize: 14, marginTop: 7 }}>
                                        {order.customer_feedback.feedback_payload}
                                    </span>
                                </div>
                            )}
                        </Card>
                    )}

                    {(canCancelOrder(order?.orderstatus_id) && order?.delivery_type === 1) && (
                        <DeliveryPartnerCard
                            order={order}
                            showToast={showToast}
                            onOrderUpdate={loadOrder}
                            setPageLoading={setLoading}
                        />
                    )}
                </div>

                <div>
                    <Card delay="2">
                        <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between" }}>
                            <Label>{isOngoing ? "Ongoing" : STATUS_MAP[order?.orderstatus_id]?.label}</Label>

                            {timingStyle.label && (
                                <span className="completion-time__label" style={{
                                    background: timingStyle.labelBg,
                                    color: timingStyle.labelColor,
                                    border: `1px solid ${timingStyle.labelColor}33`,
                                }}>
                                {timingStyle.label}
                            </span>
                            )}
                        </div>

                        <div className="completion-time">
                            {days > 0 && (
                                <>
                                    <span className="completion-time__num" style={{ color: timingStyle.color }}>{days}</span>
                                    <span className="completion-time__unit">day{days !== 1 ? "s" : ""}</span>
                                </>
                            )}
                            {(days > 0 || hours > 0) && (
                                <>
                                    <span className="completion-time__num" style={{ color: timingStyle.color }}>{String(hours).padStart(2, "0")}</span>
                                    <span className="completion-time__unit">hr</span>
                                </>
                            )}
                            <span className="completion-time__num" style={{ color: timingStyle.color }}>{String(minutes).padStart(2, "0")}</span>
                            <span className="completion-time__unit">min</span>
                            <span className="completion-time__num" style={{ color: timingStyle.color }}>{String(seconds).padStart(2, "0")}</span>
                            <span className="completion-time__unit">sec</span>
                        </div>

                        {order?.todo_note && (
                            <div style={{
                                marginTop: 14,
                                display: 'flex',
                                alignItems: 'center',
                                gap: 5,
                            }}>
                                <StickyNote size={12} color="#92400e" />
                                <span style={{
                                    fontSize: 11,
                                    color: '#92400e',
                                    fontWeight: 600,
                                }}>
            {order.todo_note.data}
        </span>
                            </div>
                        )}
                    </Card>

                    <Card delay="3">
                        <SectionTitle>Activity Log</SectionTitle>
                        {activities?.length > 0
                            ? [...activities].reverse().map((item, idx) => (
                                <TimelineItem
                                    key={idx}
                                    time={item?.created_at}
                                    text={`${item?.description} by ${item?.causer?.name}`}
                                    properties={item?.properties}   // ← add this
                                    isLast={idx === activities.length - 1}
                                    order={order}
                                />
                            ))
                            : <p className="timeline-empty">No activity recorded.</p>
                        }
                    </Card>

                    <OfferedOrdersCard offeredOrders={order?.offered_orders} />

                    <Card delay="4">
                        <Label>Delivery Route</Label>
                        <DeliveryMap order={order} />
                    </Card>

                </div>
            </div>

            {loading && (
                <div className="loading-overlay">
                    <div className="loading-overlay__box">
                        <div className="spinner"/>
                        <span className="loading-overlay__text">{loading}</span>
                    </div>
                </div>
            )}
            {toast && (
                <div className={`toast toast--${toast?.type}`}>
                    {toast?.type === 'success' ? (
                            <Check className="toast__icon" size={18} />
                        ) : (
                            <X className="toast__icon" size={18} />
                        )
                    }
                    {toast?.message}
                </div>
            )}

            {modalOpen && (
                <EditOrderModal
                    order={data?.order}
                    onSave={handleEdit}
                    onClose={()=> setModalOpen(null)}
                    existingItems={data?.order?.orderitems}
                    showToast={showToast}
                />
            )}

            <PrintBill ref={printRef} thermalRef={thermalRef} order={order} />

            {addressModalOpen && (
                <ChangeAddressModal
                    order={order}
                    onClose={() => setAddressModalOpen(false)}
                    onUpdated={loadOrder}
                    showToast={showToast}
                />
            )}
        </div>
    );
};

export default OrderPage;